import { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { 
  submitNewRating, 
  selectSubmitStatus, 
  selectSubmitError,
  resetSubmitStatus 
} from '../../../redux/slices/ratingSlice'; 
import { 
  fetchOverallStats, 
  fetchCategoryStats,
  selectOverallStats,
  selectCategoryStats,
  selectStatsLoading,
  selectStatsError
} from '../../../redux/slices/statsSlice';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Snackbar,
  Alert,
  LinearProgress,
  IconButton,
  CircularProgress,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { useParams } from "react-router-dom";

// ── Category ratings ───────────────────────────────────────────────
const RATE_CATEGORIES = [
  { id: "food", label: "Food Quality", emoji: "🍽️" },
  { id: "service", label: "Service", emoji: "🤝" },
  { id: "speed", label: "Order Speed", emoji: "⚡" },
  { id: "app", label: "App Experience", emoji: "📱" },
];

// ── Quick tag chips ────────────────────────────────────────────────
const POSITIVE_TAGS = [
  "Delicious food 😋",
  "Fast delivery 🚀",
  "Great value 💰",
  "Friendly service 😊",
  "Easy to use 📱",
  "Accurate order ✅",
];
const NEGATIVE_TAGS = [
  "Slow delivery ⏰",
  "Wrong order ❌",
  "App issues 📱",
  "Price too high 💸",
  "Poor packaging 📦",
  "Hard to navigate 🗺️",
];

// ── Rating label ───────────────────────────────────────────────────
const getRatingLabel = (r) => {
  if (!r) return { text: "Tap to rate", color: "#bbb", emoji: "⭐" };
  if (r === 1) return { text: "Poor", color: "#c62828", emoji: "😞" };
  if (r === 2) return { text: "Fair", color: "#e65100", emoji: "😐" };
  if (r === 3) return { text: "Good", color: "#f57c00", emoji: "🙂" };
  if (r === 4) return { text: "Great!", color: "#388e3c", emoji: "😄" };
  return { text: "Excellent!", color: "#2e7d32", emoji: "🤩" };
};

// ── Star row component ─────────────────────────────────────────────
const StarRow = ({ value, onChange, size = 36 }) => (
  <Box sx={{ display: "flex", gap: 0.5 }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <IconButton
        key={s}
        onClick={() => onChange(s)}
        size="small"
        sx={{
          p: 0.3,
          color: s <= value ? "#f59e0b" : "#e0e0e0",
          transition: "transform 0.1s, color 0.15s",
          "&:active": { transform: "scale(0.88)" },
        }}
      >
        {s <= value ? (
          <StarIcon sx={{ fontSize: size }} />
        ) : (
          <StarBorderIcon sx={{ fontSize: size }} />
        )}
      </IconButton>
    ))}
  </Box>
);

// ══════════════════════════════════════════════════════════════════
const RateAppPage = ({ onBack }) => {
  const dispatch = useDispatch();
  const { restaurantId } = useParams();
  // Local state
  const [mainRating, setMainRating] = useState(0);
  const [catRatings, setCatRatings] = useState({});
  const [selectedTags, setSelectedTags] = useState([]);
  const [reviewText, setReviewText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  
  // Redux state
  const submitStatus = useSelector(selectSubmitStatus);
  const submitError = useSelector(selectSubmitError);
  const overallStats = useSelector(selectOverallStats);
  const categoryStats = useSelector(selectCategoryStats);
  const statsLoading = useSelector(selectStatsLoading);
  const statsError = useSelector(selectStatsError);

  useEffect(() => {
  if (!restaurantId) return;

  dispatch(fetchOverallStats(restaurantId));
  dispatch(fetchCategoryStats(restaurantId));
}, [dispatch, restaurantId]);

  // Handle submit status
  useEffect(() => {
    if (submitStatus === 'succeeded') {
      setSubmitted(true);
      setToast({
        open: true,
        msg: "Thank you for your feedback! 💚",
        severity: "success",
      });
      dispatch(resetSubmitStatus());
    } else if (submitStatus === 'failed' && submitError) {
      setToast({
        open: true,
        msg: submitError.message || "Failed to submit rating",
        severity: "error",
      });
    }
  }, [submitStatus, submitError, dispatch]);

  // Handle stats error
  useEffect(() => {
    if (statsError.overall || statsError.categories) {
      console.error("Stats error:", statsError);
    }
  }, [statsError]);

  const handleSubmit = () => {
    if (!mainRating) {
      setToast({
        open: true,
        msg: "Please give a star rating first ⭐",
        severity: "warning",
      });
      return;
    }

    const ratingData = {
      restaurantId,
      mainRating,
      reviewText,
      selectedTags,
      categoryRatings: Object.entries(catRatings).map(([id, rating]) => ({
        categoryId: id,
        rating
      }))
    };

    dispatch(submitNewRating(ratingData));
  };

  const label = getRatingLabel(mainRating);
  const isPositive = mainRating >= 4;
  const tags = isPositive
    ? POSITIVE_TAGS
    : mainRating > 0
      ? NEGATIVE_TAGS
      : POSITIVE_TAGS;

  const toggleTag = (tag) =>
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );

  const handleCatRating = (id, val) =>
    setCatRatings((prev) => ({ ...prev, [id]: val }));

  // Calculate distribution percentages from real data
  const getDistributionArray = () => {
    if (!overallStats?.distribution) return [];
    
    return [5, 4, 3, 2, 1].map(stars => ({
      stars,
      pct: overallStats.distribution[stars]?.percentage || 0,
      count: overallStats.distribution[stars]?.count || 0
    }));
  };

  // ── Loading state ──
  if (statsLoading.overall || statsLoading.categories) {
    return (
      <Box sx={{ 
        bgcolor: "#f5f5f5", 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center" 
      }}>
        <CircularProgress sx={{ color: "#f59e0b" }} />
      </Box>
    );
  }

  // ── Submitted state ──────────────────────────────────────────
  if (submitted) {
    return (
      <Box
        sx={{
          bgcolor: "#f5f5f5",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 90,
            height: 90,
            borderRadius: "50%",
            bgcolor: "#e8f5e9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            boxShadow: "0 0 0 12px rgba(46,125,50,0.08)",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 50, color: "#2e7d32" }} />
        </Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: "#1a1a1a", mb: 0.8 }}
        >
          Thank You! 🙏
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "#555", mb: 0.5, fontSize: "0.9rem" }}
        >
          Your review helps us improve.
        </Typography>
        <Box
          sx={{ display: "flex", gap: 0.3, justifyContent: "center", mb: 2.5 }}
        >
          {[1, 2, 3, 4, 5].map((s) => (
            <StarIcon
              key={s}
              sx={{
                fontSize: 28,
                color: s <= mainRating ? "#f59e0b" : "#e0e0e0",
              }}
            />
          ))}
        </Box>
        {reviewText && (
          <Box
            sx={{
              p: 2,
              bgcolor: "white",
              borderRadius: 2.5,
              border: "1px solid #f0f0f0",
              maxWidth: 320,
              mb: 2.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#555",
                fontSize: "0.82rem",
                fontStyle: "italic",
                lineHeight: 1.6,
              }}
            >
              "{reviewText}"
            </Typography>
          </Box>
        )}
        <Chip
          icon={<ThumbUpIcon sx={{ fontSize: "16px !important" }} />}
          label={`${overallStats?.totalRatings?.toLocaleString() || 1285} people have rated this restaurant`}
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            fontSize: "0.72rem",
          }}
        />
      </Box>
    );
  }

  const distributionArray = getDistributionArray();

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", pb: 3 }}>
      {/* Top bar handled by RestaurantLayout */}

      {/* ── Overall Rating Stats ── */}
      <Box sx={{ mx: 2, mt: 2, mb: 2 }}>
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 3,
            p: 2,
            border: "1px solid #f0f0f0",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Big score */}
            <Box sx={{ textAlign: "center", flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: "2.8rem",
                  fontWeight: 900,
                  color: "#f59e0b",
                  lineHeight: 1,
                }}
              >
                {overallStats?.averageRating?.toFixed(1) || "4.3"}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 0.3 }}>
                {[1, 2, 3, 4].map((s) => (
                  <StarIcon key={s} sx={{ fontSize: 14, color: "#f59e0b" }} />
                ))}
                <StarHalfIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "#888", fontSize: "0.65rem" }}
              >
                {overallStats?.totalRatings?.toLocaleString() || "1,284"} ratings
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              {distributionArray.map(({ stars, pct, count }) => (
                <Box
                  key={stars}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                    mb: 0.3,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      color: "#555",
                      minWidth: 6,
                    }}
                  >
                    {stars}
                  </Typography>
                  <StarIcon sx={{ fontSize: 11, color: "#f59e0b" }} />
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: "#f5f5f5",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: pct > 30 ? "#f59e0b" : "#e0e0e0",
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: "#aaa",
                      minWidth: 22,
                      textAlign: "right",
                    }}
                  >
                    {pct}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 1.8 }}>
        {/* ── Main Star Rating ── */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: 2.5,
            p: 2.5,
            border: "1px solid #f0f0f0",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "0.88rem",
              color: "#333",
              mb: 1.5,
            }}
          >
            How would you rate your experience?
          </Typography>
          <StarRow value={mainRating} onChange={setMainRating} size={40} />
          <Typography
            variant="body2"
            sx={{
              mt: 1.2,
              fontWeight: 700,
              color: label.color,
              fontSize: "0.95rem",
            }}
          >
            {label.emoji} {label.text}
          </Typography>
        </Box>

        {/* ── Category Ratings (shown after main rating) ── */}
        {mainRating > 0 && (
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 2.5,
              p: 2,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#333",
                mb: 1.5,
              }}
            >
              Rate specific aspects:
            </Typography>
            {RATE_CATEGORIES.map((cat) => (
              <Box
                key={cat.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.2,
                  "&:last-child": { mb: 0 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                  <Typography sx={{ fontSize: "1.1rem" }}>
                    {cat.emoji}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", color: "#444", fontWeight: 500 }}
                  >
                    {cat.label}
                  </Typography>
                </Box>
                <StarRow
                  value={catRatings[cat.id] || 0}
                  onChange={(v) => handleCatRating(cat.id, v)}
                  size={22}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* ── Tag Chips ── */}
        {mainRating > 0 && (
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 2.5,
              p: 2,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#333",
                mb: 1.2,
              }}
            >
              {isPositive ? "What did you love? 💚" : "What can we improve? 🔧"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onClick={() => toggleTag(tag)}
                  sx={{
                    height: 28,
                    fontSize: "0.73rem",
                    fontWeight: selectedTags.includes(tag) ? 700 : 500,
                    bgcolor: selectedTags.includes(tag)
                      ? isPositive
                        ? "#e8f5e9"
                        : "#ffebee"
                      : "#f5f5f5",
                    color: selectedTags.includes(tag)
                      ? isPositive
                        ? "#2e7d32"
                        : "#c62828"
                      : "#555",
                    border: selectedTags.includes(tag)
                      ? `1.5px solid ${isPositive ? "#a5d6a7" : "#ef9a9a"}`
                      : "1px solid #e0e0e0",
                    transition: "all 0.15s",
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* ── Written Review ── */}
        {mainRating > 0 && (
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 2.5,
              p: 2,
              border: "1px solid #f0f0f0",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: "0.85rem",
                color: "#333",
                mb: 1.2,
              }}
            >
              Share more details (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Tell us about your experience…"
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
              helperText={`${reviewText.length}/500`}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.83rem",
                  "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
                },
              }}
            />
          </Box>
        )}

        {/* ── Submit ── */}
        <Button
          fullWidth
          variant="contained"
          endIcon={submitStatus === 'loading' ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleSubmit}
          disabled={!mainRating || submitStatus === 'loading'}
          sx={{
            py: 1.5,
            borderRadius: 2.5,
            bgcolor: mainRating ? "#f59e0b" : "#e0e0e0",
            color: mainRating ? "white" : "#aaa",
            "&:hover": { bgcolor: mainRating ? "#e65100" : "#e0e0e0" },
            textTransform: "none",
            fontWeight: 800,
            fontSize: "0.95rem",
            boxShadow: mainRating ? "0 4px 16px rgba(245,158,11,0.4)" : "none",
            transition: "all 0.2s",
          }}
        >
          {submitStatus === 'loading' 
            ? "Submitting..." 
            : mainRating
              ? `Submit ${label.emoji} Review`
              : "Select a Rating First"}
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: 70 }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: "0.8rem" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RateAppPage;
